// src/components/forms/SmartArrayInput.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, ChevronDown } from 'lucide-react';

const SmartArrayInput = ({ 
  label, 
  values = [], 
  existingOptions = [], 
  onChange, 
  placeholder = "Enter or select...",
  className = ""
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Filter existing options based on input value and exclude already selected values
    const filtered = existingOptions.filter(option => 
      option.toLowerCase().includes(inputValue.toLowerCase()) &&
      !values.includes(option)
    ).slice(0, 10); // Limit to 10 suggestions
    
    setFilteredOptions(filtered);
  }, [inputValue, existingOptions, values]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddValue = (value) => {
    const trimmedValue = value.trim();
    if (trimmedValue && !values.includes(trimmedValue)) {
      onChange([...values, trimmedValue]);
      setInputValue('');
      setShowDropdown(false);
    }
  };

  const handleRemoveValue = (index) => {
    onChange(values.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddValue(inputValue);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-600 mb-2">
        {label}
      </label>
      
      {/* Display selected values */}
      <div className="space-y-2 mb-2">
        {values.map((value, index) => (
          <div key={index} className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
            <span className="text-sm text-blue-800">{value}</span>
            <button
              type="button"
              onClick={() => handleRemoveValue(index)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Input field with dropdown */}
      <div className="relative">
        <div className="flex">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={() => inputValue.trim() && handleAddValue(inputValue)}
            className="px-3 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Dropdown for existing options */}
        {showDropdown && (filteredOptions.length > 0 || existingOptions.length > 0) && (
          <div 
            ref={dropdownRef}
            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
          >
            {filteredOptions.length > 0 ? (
              <>
                <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b">
                  Existing {label.toLowerCase()}
                </div>
                {filteredOptions.map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleAddValue(option)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  >
                    {option}
                  </button>
                ))}
              </>
            ) : inputValue.trim() ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                Press Enter to add "{inputValue.trim()}"
              </div>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-400">
                Start typing to see suggestions or enter new {label.toLowerCase()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartArrayInput;